// ============================================================================
// Componente: FinanceiroNumerarioDiario.js
// Objetivo: vendido × declarado, POR DIA e loja — o drill-down da conferência.
//
// ⚠️ ESTA TELA NÃO DÁ VEREDITO POR DIA, e isso é decisão, não esquecimento.
//   O Flex fecha por `business_date` (dia operacional) e a Sovos por relógio.
//   Nas lojas 24h — ANV, VEL e SED — a venda da madrugada cai em dias diferentes
//   nas duas bases, então um dia isolado quase nunca fecha exato. Na semana isso
//   se anula (é por isso que a conferência semanal existe). Aqui os dois números
//   ficam lado a lado para leitura humana: serve para achar EM QUE DIA a
//   diferença da semana nasceu, não para acusar o dia.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { CalendarRange, Loader2, AlertTriangle, Info } from 'lucide-react';
import {
    COR, COR_CLARA, COR_BORDA, COR_NEUTRA,
    dinheiro, dataBR, corDaDiferenca, th, td, tdNum,
} from './numerarioFormato';

/** AAAA-MM-DD de hoje menos N dias (sem depender de biblioteca de data). */
const isoMenos = (dias) => {
    const d = new Date();
    d.setDate(d.getDate() - dias);
    return d.toISOString().slice(0, 10);
};

const FinanceiroNumerarioDiario = () => {
    const [inicio, setInicio] = useState(isoMenos(14));
    const [fim, setFim] = useState(isoMenos(1));
    const [loja, setLoja] = useState('');
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);

    const token = () => localStorage.getItem('token') || localStorage.getItem('authToken');

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const url = `/api/numerario/diario?inicio=${inicio}&fim=${fim}${loja ? `&loja=${loja}` : ''}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
            const json = await res.json();
            if (!res.ok) { setErro(json.erro || 'Falha ao carregar.'); setDados(null); return; }
            setDados(json);
        } catch {
            setErro('Não foi possível falar com o servidor.');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, [inicio, fim, loja]);

    useEffect(() => { carregar(); }, [carregar]);

    const linhas = dados?.linhas || [];
    const lojas = [...new Set(linhas.map((l) => l.loja))].sort();

    // Totais por loja, para a linha de fecho — é a soma do período que importa,
    // não o dia.
    const totalVendido = linhas.reduce((a, l) => a + (l.vendido || 0), 0);
    const totalDeclarado = linhas.reduce((a, l) => a + (l.declarado || 0), 0);

    return (
        <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
            <header style={{ marginBottom: 18 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: COR, margin: 0 }}>
                    <CalendarRange size={22} /> Vendido × declarado por dia
                </h2>
                <p style={{ color: '#475569', marginTop: 6, fontSize: 14, maxWidth: 900 }}>
                    O detalhe diário da conferência: serve para achar <strong>em que dia</strong> nasceu
                    a diferença da semana.
                </p>
            </header>

            <section style={{
                background: COR_CLARA, border: `1px solid ${COR_BORDA}`, borderRadius: 12,
                padding: 16, marginBottom: 16, display: 'flex', gap: 14,
                alignItems: 'center', flexWrap: 'wrap',
            }}>
                <label style={rotulo}>De</label>
                <input type="date" value={inicio} max={fim}
                    onChange={(e) => setInicio(e.target.value)} style={campo} />
                <label style={rotulo}>até</label>
                <input type="date" value={fim} min={inicio}
                    onChange={(e) => setFim(e.target.value)} style={campo} />
                <label style={rotulo}>Loja</label>
                <select value={loja} onChange={(e) => setLoja(e.target.value)} style={{ ...campo, minWidth: 130 }}>
                    <option value="">Todas</option>
                    {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                {carregando && <Loader2 size={18} className="animate-spin" color={COR} />}
            </section>

            <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16,
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '10px 14px', color: '#475569', fontSize: 13, lineHeight: 1.55,
            }}>
                <Info size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                    Dia isolado quase nunca fecha exato: o Flex fecha por dia operacional e a Sovos
                    pelo relógio, então a venda da madrugada das lojas 24h (ANV, VEL, SED) cai em
                    dias diferentes nas duas bases. Na semana a diferença se anula — por isso aqui
                    não há classificação de sobra ou falta.
                </span>
            </div>

            {erro && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'center', background: '#fef2f2',
                    border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10,
                    padding: '12px 16px', marginBottom: 16, fontSize: 14,
                }}>
                    <AlertTriangle size={18} /> {erro}
                </div>
            )}

            {linhas.length > 0 && (
                <>
                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Dia</th>
                                    <th style={th}>Loja</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Vendido</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Declarado</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Diferença</th>
                                    <th style={{ ...th, textAlign: 'right', color: COR_NEUTRA }}
                                        title="Troco e câmbio — dinheiro que passou pelo caixa sem ser venda.">
                                        Troco/câmbio
                                    </th>
                                    <th style={{ ...th, textAlign: 'right', color: COR_NEUTRA }}
                                        title="Depositado direto no BANESTES, fora do malote da Prosegur.">
                                        Banestes
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((l) => (
                                    <tr key={`${l.dia}-${l.loja}`}>
                                        <td style={td}>{dataBR(l.dia)}</td>
                                        <td style={td}><strong>{l.loja}</strong></td>
                                        <td style={tdNum}>{dinheiro(l.vendido)}</td>
                                        <td style={tdNum}>{dinheiro(l.declarado)}</td>
                                        <td style={{ ...tdNum, color: corDaDiferenca(l.diferenca) }}>
                                            {dinheiro(l.diferenca)}
                                        </td>
                                        <td style={{ ...tdNum, color: COR_NEUTRA }}>
                                            {l.naoVenda ? dinheiro(l.naoVenda) : '—'}
                                        </td>
                                        <td style={{ ...tdNum, color: COR_NEUTRA }}>
                                            {l.banestes ? dinheiro(l.banestes) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#f8fafc' }}>
                                    <td style={{ ...td, fontWeight: 700 }} colSpan={2}>
                                        Total do período{loja ? ` — ${loja}` : ''}
                                    </td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{dinheiro(totalVendido)}</td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{dinheiro(totalDeclarado)}</td>
                                    <td style={{
                                        ...tdNum, fontWeight: 700,
                                        color: corDaDiferenca(totalDeclarado - totalVendido),
                                    }}>
                                        {dinheiro(totalDeclarado - totalVendido)}
                                    </td>
                                    <td colSpan={2} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 10 }}>
                        {linhas.length} linha(s) · {lojas.length} loja(s)
                    </p>
                </>
            )}

            {dados && linhas.length === 0 && !carregando && (
                <p style={{ color: '#94a3b8', fontSize: 14 }}>Nenhum movimento no período.</p>
            )}
        </div>
    );
};

const rotulo = { fontWeight: 600, color: COR, fontSize: 14 };
const campo = {
    padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
    fontSize: 14, background: '#fff', color: '#0f172a',
};

export default FinanceiroNumerarioDiario;
